import { AuthService } from '@/auth/service/auth.service';
import { ReportsService } from '@/services/reports/reports.service';
import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { combineLatest, Subject } from 'rxjs';
import { SelectedListToFilter, MultipleIdListToFilter } from '../../../interfaces/reports/reports';
import { CommonService } from '../../../services/common/common.service';
import { InfractionService } from '../../../services/infraction/infraction.service';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { getCurrentUserData } from '@/store/login/selectors';
import { Store } from '@ngrx/store';
import { StoreState } from '@/store';

type View = 'requests' | 'licenses' | 'infractions';

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.css'],
})
export class ReportsComponent implements OnInit {

  assetList: SelectedListToFilter[] = [];
  typeRequestList: SelectedListToFilter[] = [];
  stateRequestList: SelectedListToFilter[] = [];

  stateLicenseList: MultipleIdListToFilter[] = [];
  zonesList: MultipleIdListToFilter[] = [];
  severityInfractionsList: MultipleIdListToFilter[] = [];
  typeSanctionList: MultipleIdListToFilter[] = [];

  assetIdSelectedControl = new FormControl([]);

  resetFormControl: Subject<boolean> = new Subject<boolean>();
  currentViewControl: FormControl = new FormControl('');
  availableViews: View[] = [];

  gettingItemsList: boolean = true;
  gettingItemsListError: string = 'DEFAULT_VALUE';

  gettingZoneList: boolean = true;
  gettingSeverityList: boolean = true;
  gettingSanctionList: boolean = true;

  currentView?: View;
  assetId: number = 1

  constructor(
    private router: Router,
    private auth: AuthService,
    private reportsServices: ReportsService,
    private commonServices: CommonService,
    private infractionsService: InfractionService,
    private store: Store<StoreState>,
  ) {

    const userData$ = getCurrentUserData(this.store);

    userData$.subscribe({
      next: () => {
        this.setCurrentView(this.router.url.split('/').reverse()[0] as View);
        this.drawComponents();
      },
    });

    this.router.events.subscribe({
      next: (val: any) => {
        if (val.url) {
          this.setCurrentView(val.url);
          this.drawComponents();
        }
      },
    });

    this.currentViewControl.valueChanges.subscribe((newView) => {
      if (newView !== this.currentView) {
        this.currentView = newView;
        this.router.navigateByUrl(`/reports/${newView}`);
      }
    });
  }

  ngOnInit(): void {
    this.getAssetListToSelect();
    this.getStateRequestToSelect();
    this.getTypesRequestToSelect();
    this.getStatetLicenseToSelect();
    // let defaultValueWasSetted = false;
    this.assetIdSelectedControl.valueChanges.pipe(
      filter((assetIds) => assetIds.length > 0),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev.sort()) === JSON.stringify(curr.sort())),
    ).subscribe({
      next: (assetIds) => {
        this.getZonesListToSelect(assetIds);
        this.getSeverityListToSelect(assetIds);
        this.getSanctionTypesListToSelect(assetIds);
      },
    });

  }

  setCurrentView(newUrl?: string) {
    if (newUrl) {
      if (newUrl !== this.currentView) {
        if (newUrl.split('/').reverse()[1] === 'reports') {
          const newView = newUrl.split('/').reverse()[0];
          this.currentViewControl.patchValue(newView);
        }
        else if (newUrl === 'requests' || newUrl === 'licenses' || newUrl === 'infractions') {
          this.currentViewControl.patchValue(newUrl);
        }
      }
    }
  }

  drawComponents() {
    const availableViews: View[] = [];
    if (this.auth.userHavePrivilege('REPORT INFRINGEMENT')) {
      availableViews.push('infractions');
    }
    if (this.auth.userHavePrivilege('REPORT LICENSE')) {
      availableViews.push('licenses');
    }
    if (this.auth.userHavePrivilege('REPORT REQUEST')) {
      availableViews.push('requests');
    }
    this.availableViews = availableViews;
  }

  getAssetListToSelect() {
    this.commonServices.getAllAssets()
      .subscribe({
        next: (res) => {
          this.gettingItemsList = false;
          const assetsToCheck = res.map((value) => ({ id: value.id, name: value.shortName, isActive: false, entryId: 0 }));
          assetsToCheck.forEach((item, index) => {
            assetsToCheck[index].isActive = false;
            assetsToCheck[index].entryId = item.id;
          });
          this.assetList = assetsToCheck;
        },
      });
  }
  getZonesListToSelect(assetIds: number[]) {
    const zoneObservables$ = assetIds.map((selected: number) => this.commonServices.getZonesByAsset(selected));
    this.gettingZoneList = true;
    combineLatest(...zoneObservables$).subscribe({
      next: (value) => {
        this.gettingItemsList = false;
        const flattedZones = value.flat();

        const cleanedZones: MultipleIdListToFilter[] = [];
        flattedZones.map((zone) => {
          const isAlreadyInArray = cleanedZones.find(({ id }) => zone.id === id);
          if (!isAlreadyInArray) {
            cleanedZones.push(zone);
          }
        });

        const cleanedZonesMergedObj = cleanedZones.reduce<Record<string, string[]>>((obj, zone) => {
          if (!(zone.name in obj)) {
            // eslint-disable-next-line no-param-reassign
            obj[zone.name] = [];
          }
          obj[zone.name].push(zone.id);
          return obj;
        }, {});

        const mergedZones: MultipleIdListToFilter[] = Object.keys(cleanedZonesMergedObj).map((key) => ({ id: cleanedZonesMergedObj[key].join(','), name: key, isActive: false }));

        this.zonesList = mergedZones;
        this.gettingZoneList = false;
      },
    });
  }
  getStatetLicenseToSelect() {

    this.reportsServices.getStateLicenseToFilter()
      .subscribe({
        next: (res) => {
          this.gettingItemsList = false;
          const stateToCheck = res.map((value) => ({ id: value.id, name: value.name, isActive: false, entryId: 0 }));

          const cleanedZonesMergedObj = stateToCheck.reduce<Record<string, string[]>>((obj, zone) => {
            if (!(zone.name in obj)) {
              // eslint-disable-next-line no-param-reassign
              obj[zone.name] = [];
            }
            obj[zone.name].push(`${zone.id}`);
            return obj;
          }, {});

          const mergedZones: MultipleIdListToFilter[] = Object.keys(cleanedZonesMergedObj).map((key) => ({ id: cleanedZonesMergedObj[key].join(','), name: key, isActive: false }));

          stateToCheck.forEach((item, index) => {
            stateToCheck[index].isActive = false;
            stateToCheck[index].entryId = item.id;
          });

          this.stateLicenseList = mergedZones;
        },
      });
  }
  getSeverityListToSelect(assetIds: number[]) {
    const severityObservables$ = assetIds.map((selected: number) => this.infractionsService.getSeverityTypes(selected));
    this.gettingSeverityList = true;

    combineLatest(...severityObservables$).subscribe({
      next: (value) => {
        this.gettingItemsList = false;
        const flattedSeverityTypes = value.flat();

        const cleanedSeverityTypes: MultipleIdListToFilter[] = [];
        flattedSeverityTypes.map((severityType) => {
          const isAlreadyInArray = cleanedSeverityTypes.find(({ id }) => severityType.id === id);
          if (!isAlreadyInArray) {
            cleanedSeverityTypes.push(severityType);
          }
        });

        const cleanedMergedObj = cleanedSeverityTypes.reduce<Record<string, string[]>>((obj, zone) => {
          if (!(zone.name in obj)) {
            // eslint-disable-next-line no-param-reassign
            obj[zone.name] = [];
          }
          obj[zone.name].push(zone.id);
          return obj;
        }, {});

        const mergedZones: MultipleIdListToFilter[] = Object.keys(cleanedMergedObj).map((key) => ({ id: cleanedMergedObj[key].join(','), name: key, isActive: false }));
        this.severityInfractionsList = mergedZones;
        this.gettingSeverityList = false;
      },
    });
  }
  getSanctionTypesListToSelect(assetIds: number[]) {
    const sanctionObservables$ = assetIds.map((selected: number) => this.infractionsService.getSanctionTypes(selected));
    this.gettingSanctionList = true;
    combineLatest(...sanctionObservables$).subscribe({
      next: (value) => {
        this.gettingItemsList = false;
        const flattedSanctionTypes = value.flat();

        const cleanedSanctionTypes: MultipleIdListToFilter[] = [];
        flattedSanctionTypes.map((sanctionType) => {
          const isAlreadyInArray = cleanedSanctionTypes.find(({ id }) => sanctionType.id === id);
          if (!isAlreadyInArray) {
            cleanedSanctionTypes.push(sanctionType);
          }
        });

        const cleanedMergedObj = cleanedSanctionTypes.reduce<Record<string, string[]>>((obj, zone) => {
          if (!(zone.name in obj)) {
            // eslint-disable-next-line no-param-reassign
            obj[zone.name] = [];
          }
          obj[zone.name].push(zone.id);
          return obj;
        }, {});

        const mergedZones: MultipleIdListToFilter[] = Object.keys(cleanedMergedObj).map((key) => ({ id: cleanedMergedObj[key].join(','), name: key, isActive: false }));

        this.typeSanctionList = mergedZones;
        this.gettingSanctionList = false;
      },
    });
  }
  getTypesRequestToSelect() {
    this.reportsServices.getTypesRequest()
      .subscribe({
        next: (res) => {
          this.gettingItemsList = false;
          const requestTypesToCheck = res.map((value) => ({ id: value.id, name: value.name, isActive: false, entryId: 0 }));
          requestTypesToCheck.forEach((item, index) => {
            requestTypesToCheck[index].isActive = false;
            requestTypesToCheck[index].entryId = item.id;
          });
          this.typeRequestList = requestTypesToCheck;
        },
      });

  }
  getStateRequestToSelect() {
    this.reportsServices.getStateRequest()
      .subscribe({
        next: (res) => {
          this.gettingItemsList = false;
          const requestStateToCheck = res.map((value) => ({ id: value.id, name: value.name, isActive: false, entryId: 0 }));
          requestStateToCheck.forEach((item, index) => {
            requestStateToCheck[index].isActive = false;
            requestStateToCheck[index].entryId = item.id;
          });
          this.stateRequestList = requestStateToCheck;
        },
      });

  }
  resetChildForm() {
    this.resetFormControl.next(true);

    //  this.myForm.onReset();

  }
}
